import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { editLoadInfo } from '../../../actions/take-off';
import { checkExistValue } from '../../../service/utils';
import { validateState, getValue, VALIDATION_EMPTY_FIELD } from '../../hooks/useForm/validate';
import { useSingleConvert } from '../../hooks/useMeasureConverter';

const VALIDATOR_MAX_VALUE = 150000;

export const useLoadInfoField = (loadInfo, recordId, name, defaultValue, onChangeValue, required, validator) => {
    const dispatch = useDispatch();
    const { t: translate } = useTranslation();
    const [errorText, changeError] = useState(required && !defaultValue ? translate('errors.validate.fieldIsRequired') : '');
    const [value, setValue] = useState(defaultValue);
    const convert = useSingleConvert();
    const [field, changeField] = useState({
        name: '',
        title: '',
        type: 'number',
        validator: { type: 'decimal', min: 0, max: convert(VALIDATOR_MAX_VALUE), precision: [7, 1] },
    });

    const handleEditField = useCallback((id, loadType, name, value) => dispatch(editLoadInfo(id, loadType, name, value)), []);

    const handleChange = useCallback((evt) => setValue(evt.target.value), []);

    useEffect(() => {
        if (checkExistValue(defaultValue)) {
            setValue(defaultValue);
            changeError((validateState(validator, defaultValue, required, translate) || {}).errorText);
        }
    }, [required, defaultValue, translate]);

    useEffect(() => {
        const updatedValidator = { ...field.validator, max: convert(VALIDATOR_MAX_VALUE) };
        changeField((prevState) => ({
            ...prevState,
            name,
            title: name,
            validator: { ...(validator || updatedValidator), isRequired: required },
        }));
    }, [validator, required, defaultValue]);

    const handleInputFocus = useCallback(() => {
        if (errorText) {
            changeError('');
        }
    }, []);

    const handleInfoChange = useCallback(
        (evt) => {
            const { validator, isRequired = false } = field;
            const fieldValue = evt.currentTarget.value === '' ? undefined : getValue(validator, evt.currentTarget.value);
            const validateRes = validateState(validator, fieldValue, isRequired, translate);

            if (validateRes !== null && validateRes.errorText !== translate(VALIDATION_EMPTY_FIELD)) {
                return changeError(validateRes.errorText);
            }

            handleEditField(recordId, loadInfo.type, evt.target.name, fieldValue);
            if (onChangeValue) {
                onChangeValue(fieldValue);
            }
        },
        [loadInfo, field, translate]
    );

    return {
        value,
        errorText,
        handleChange,
        handleInfoChange,
        handleInputFocus,
    };
};
